import React, { useEffect, useMemo, useRef, useState } from 'react';
import { askVim, OCR_QUICK_QUERIES } from '../../utils/ocrApi';
import '../../Styles/OCRChatWidget.css';

const OCR_WIDGET_STORAGE_KEY = 'ocr_widget_state_v1';

const WELCOME_MESSAGE = {
  id: 'welcome',
  sender: 'ai',
  text: 'Hi, I am your Invoice AI Assistant. Ask me about invoice statuses, vendor payments, SAP sync, and more.',
};

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function cleanFormatting(text) {
  if (!text) return '';
  text = text.replace(/\s*dtype:\s*\w+\s*[\r\n]*/g, '');
  text = text.replace(/^\s*\d+\s+/gm, '');
  text = text.replace(/^#+\s*(.*?)\s*#*$/gm, '<strong>$1</strong>');
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
  text = text.replace(/\\n/g, '\n');
  text = text.replace(/^\s*[-*+]\s+(.*)/gm, '• $1');
  text = text.replace(/\n/g, '<br/>');
  return text;
}

function parseResponse(data) {
  if (!data) return '';
  if (typeof data === 'string') {
    try { return parseResponse(JSON.parse(data)); } catch { return cleanFormatting(data); }
  }

  // error
  if (data.error) return `<span style="color:#dc2626">Error: ${data.error}</span>`;

  let html = '';

  // Main answer — check all known fields in same order as Chat.js
  const possibleFields = ['answer_text', 'response', 'message', 'content', 'answer', 'text'];
  for (const field of possibleFields) {
    if (data[field]) { html += cleanFormatting(data[field]); break; }
  }

  // Clarification (shown if different from answer)
  if (data.clarification && data.clarification !== data.answer_text) {
    html += `<div style="margin-top:8px;padding:8px 10px;background:#fffbeb;border-left:3px solid #f59e0b;border-radius:4px;font-size:0.9em;color:#92400e">${cleanFormatting(data.clarification)}</div>`;
  }

  // Data table
  if (Array.isArray(data.data) && data.data.length > 0) {
    const cols = Object.keys(data.data[0]);
    html += `<div style="overflow:auto;margin-top:10px"><table style="width:100%;border-collapse:collapse;font-size:0.85em">`;
    html += `<thead><tr>${cols.map((c) => `<th style="padding:6px 8px;background:#f1f5f9;border:1px solid #e2e8f0;font-weight:700;white-space:nowrap">${c}</th>`).join('')}</tr></thead>`;
    html += `<tbody>${data.data.map((row) => `<tr>${cols.map((c) => `<td style="padding:6px 8px;border:1px solid #e2e8f0">${row[c] ?? '—'}</td>`).join('')}</tr>`).join('')}</tbody>`;
    html += `</table></div>`;
    if (data.total_count) html += `<div style="margin-top:6px;font-size:0.8em;color:#64748b">Total: ${data.total_count}</div>`;
  }

  // Suggested followups
  if (Array.isArray(data.suggested_followups) && data.suggested_followups.length > 0) {
    html += `<div style="margin-top:10px;display:flex;flex-wrap:wrap;gap:6px">`;
    data.suggested_followups.forEach((f) => {
      html += `<span style="font-size:0.78em;padding:3px 10px;border-radius:999px;background:#eff6ff;border:1px solid #bfdbfe;color:#1d4ed8;cursor:pointer">${f}</span>`;
    });
    html += `</div>`;
  }

  if (!html) return cleanFormatting(JSON.stringify(data, null, 2));
  return html;
}

function buildSessionId() {
  return `ocr-widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeSessionTitle(question) {
  const clean = (question || '').trim();
  if (!clean) return 'New chat';
  return clean.length > 30 ? `${clean.slice(0, 30)}...` : clean;
}

function buildSession(title = 'New chat') {
  return {
    id: buildSessionId(),
    title,
    updatedAt: Date.now(),
    conversationHistory: [],
    messages: [WELCOME_MESSAGE],
  };
}

function getInitialState() {
  try {
    const raw = localStorage.getItem(OCR_WIDGET_STORAGE_KEY);
    if (!raw) {
      const initialSession = buildSession('Chat 1');
      return {
        open: true,
        expanded: false,
        fullScreen: false,
        activeSessionId: initialSession.id,
        sessions: [initialSession],
      };
    }

    const parsed = JSON.parse(raw);
    const savedSessions = Array.isArray(parsed.sessions) && parsed.sessions.length > 0
      ? parsed.sessions
      : [buildSession('Chat 1')];

    const activeSessionId = savedSessions.some((session) => session.id === parsed.activeSessionId)
      ? parsed.activeSessionId
      : savedSessions[0].id;

    return {
      open: typeof parsed.open === 'boolean' ? parsed.open : true,
      expanded: typeof parsed.expanded === 'boolean' ? parsed.expanded : false,
      fullScreen: typeof parsed.fullScreen === 'boolean' ? parsed.fullScreen : false,
      activeSessionId,
      sessions: savedSessions,
    };
  } catch (_error) {
    const initialSession = buildSession('Chat 1');
    return {
      open: true,
      expanded: false,
      fullScreen: false,
      activeSessionId: initialSession.id,
      sessions: [initialSession],
    };
  }
}

const QUICK_PROMPTS = OCR_QUICK_QUERIES.map((query) => query.prompt).slice(0, 4);

const OCRChatWidget = () => {
  const initial = useMemo(() => getInitialState(), []);
  const [open, setOpen] = useState(initial.open);
  const [expanded, setExpanded] = useState(initial.expanded);
  const [fullScreen, setFullScreen] = useState(initial.fullScreen);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState(initial.sessions);
  const [activeSessionId, setActiveSessionId] = useState(initial.activeSessionId);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const messagesEndRef = useRef(null);

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) || sessions[0],
    [sessions, activeSessionId]
  );

  const messages = activeSession?.messages || [WELCOME_MESSAGE];

  useEffect(() => {
    localStorage.setItem(
      OCR_WIDGET_STORAGE_KEY,
      JSON.stringify({
        open,
        expanded,
        fullScreen,
        activeSessionId,
        sessions,
      })
    );
  }, [open, expanded, fullScreen, activeSessionId, sessions]);

  useEffect(() => {
    if (!fullScreen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setFullScreen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [fullScreen]);

  useEffect(() => {
    if (fullScreen) {
      document.body.classList.add('ocr-widget-fullscreen-active');
    } else {
      document.body.classList.remove('ocr-widget-fullscreen-active');
    }

    return () => {
      document.body.classList.remove('ocr-widget-fullscreen-active');
    };
  }, [fullScreen]);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [open, messages, loading]);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const updateActiveSession = (patch) => {
    setSessions((prev) => prev.map((session) => (
      session.id === activeSessionId
        ? { ...session, ...patch, updatedAt: Date.now() }
        : session
    )));
  };

  const sendMessage = async (question) => {
    const clean = (question || '').trim();
    if (!clean || loading) return;

    const userMessage = {
      id: `u-${Date.now()}`,
      sender: 'user',
      text: clean,
      time: nowLabel(),
    };

    const nextTitle = activeSession?.messages?.length <= 1 ? makeSessionTitle(clean) : activeSession?.title;
    const nextMessages = [...messages, userMessage];

    updateActiveSession({
      title: nextTitle,
      messages: nextMessages,
    });

    setInput('');
    setError('');
    setLoading(true);

    try {
      const history = activeSession?.conversationHistory || [];
      const data = await askVim(clean, null, history);

      const reply = parseResponse(data);

      const aiMessage = {
        id: `a-${Date.now()}`,
        sender: 'ai',
        text: reply,
        time: nowLabel(),
      };

      const nextHistory = [
        ...history,
        { role: 'user', content: clean },
        { role: 'assistant', content: reply },
      ];

      updateActiveSession({
        messages: [...nextMessages, aiMessage],
        conversationHistory: nextHistory,
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch chatbot response');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (canSend) {
      sendMessage(input);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) {
        sendMessage(input);
      }
    }
  };

  const handleNewChat = () => {
    const nextSession = buildSession(`Chat ${sessions.length + 1}`);
    setSessions((prev) => [nextSession, ...prev]);
    setActiveSessionId(nextSession.id);
    setInput('');
    setError('');
    setLoading(false);
  };

  const handleClearChat = () => {
    updateActiveSession({
      title: 'New chat',
      messages: [WELCOME_MESSAGE],
      conversationHistory: [],
    });
    setError('');
  };

  const handleStartRenameSession = (session) => {
    setEditingSessionId(session.id);
    setRenameValue(session.title || 'New chat');
  };

  const handleCancelRenameSession = () => {
    setEditingSessionId(null);
    setRenameValue('');
  };

  const handleSaveRenameSession = (sessionId) => {
    const nextTitle = (renameValue || '').trim();
    if (!nextTitle) return;

    setSessions((prev) => prev.map((session) => (
      session.id === sessionId
        ? { ...session, title: nextTitle, updatedAt: Date.now() }
        : session
    )));

    handleCancelRenameSession();
  };

  const handleDeleteSession = (sessionId) => {
    setSessions((prev) => {
      const remaining = prev.filter((session) => session.id !== sessionId);

      if (remaining.length === 0) {
        const fallback = buildSession('Chat 1');
        setActiveSessionId(fallback.id);
        return [fallback];
      }

      if (activeSessionId === sessionId) {
        setActiveSessionId(remaining[0].id);
      }

      return remaining;
    });

    if (editingSessionId === sessionId) {
      handleCancelRenameSession();
    }
  };

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0)),
    [sessions]
  );

  return (
    <div className={`ocr-widget-container ${open ? 'is-open' : ''} ${fullScreen ? 'fullscreen' : ''}`}>
      {!open && (
        <button
          type="button"
          className="ocr-widget-toggle"
          onClick={() => setOpen(true)}
          aria-label="Open Invoice AI chat assistant"
        >
          Ask Invoice AI
        </button>
      )}

      {open && (
        <section
          className={`ocr-widget-panel ${expanded ? 'expanded' : ''}`}
          aria-label="Invoice AI Chat Widget"
        >
          <header className="ocr-widget-header">
            <div className="ocr-widget-title">
              <h3>Invoice AI Assistant</h3>
              <p>{loading ? 'Analyzing...' : 'Live API answers'}</p>
            </div>
            <div className="ocr-widget-actions">
              <button type="button" onClick={handleNewChat}>New</button>
              <button type="button" onClick={handleClearChat}>Clear</button>
              <button type="button" onClick={() => setExpanded((prev) => !prev)}>{expanded ? 'Min' : 'Max'}</button>
              {fullScreen ? (
                <button type="button" onClick={() => setFullScreen(false)}>Exit</button>
              ) : (
                <button type="button" onClick={() => { setExpanded(true); setFullScreen(true); }}>Full</button>
              )}
              <button type="button" onClick={() => { setOpen(false); setFullScreen(false); }}>Close</button>
            </div>
          </header>

          <div className="ocr-widget-body">
            {expanded && (
              <aside className="ocr-widget-sessions" aria-label="Chat sessions">
                <h4>Sessions</h4>
                <div className="ocr-widget-session-list">
                  {sortedSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`ocr-widget-session-item ${session.id === activeSessionId ? 'active' : ''}`}
                    >
                      {editingSessionId === session.id ? (
                        <div className="ocr-widget-session-rename" onClick={(event) => event.stopPropagation()}>
                          <input
                            value={renameValue}
                            onChange={(event) => setRenameValue(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSaveRenameSession(session.id);
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                handleCancelRenameSession();
                              }
                            }}
                            autoFocus
                          />
                          <div className="ocr-widget-session-rename-actions">
                            <button type="button" onClick={() => handleSaveRenameSession(session.id)}>Save</button>
                            <button type="button" onClick={handleCancelRenameSession}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="ocr-widget-session-main"
                            onClick={() => {
                              setActiveSessionId(session.id);
                              setError('');
                            }}
                          >
                            <strong>{session.title || 'New chat'}</strong>
                            <span>{new Date(session.updatedAt || Date.now()).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </button>
                          <div className="ocr-widget-session-item-actions">
                            <button type="button" onClick={() => handleStartRenameSession(session)}>Edit</button>
                            <button type="button" onClick={() => handleDeleteSession(session.id)}>Del</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </aside>
            )}

            <div className="ocr-widget-main">
              <div className="ocr-widget-messages">
                {messages.length <= 1 && (
                  <div className="ocr-widget-quick-prompts">
                    {QUICK_PROMPTS.map((prompt) => (
                      <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>{prompt}</button>
                    ))}
                  </div>
                )}

                {messages.map((message) => (
                  <article key={message.id} className={`ocr-widget-message ${message.sender === 'user' ? 'user' : 'ai'}`}>
                    <div className="ocr-widget-bubble">
                      {message.sender === 'ai' ? (
                        <p dangerouslySetInnerHTML={{ __html: message.text }} />
                      ) : (
                        <p>{message.text}</p>
                      )}
                      <small className="ocr-widget-time">{message.time || ''}</small>
                    </div>
                  </article>
                ))}

                {loading && (
                  <div className="ocr-widget-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {error && <p className="ocr-widget-error">{error}</p>}

              <form className="ocr-widget-input" onSubmit={handleSubmit}>
                <textarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Ask about invoices, payments, SAP status..."
                />
                <button type="submit" disabled={!canSend}>{loading ? 'Sending...' : 'Send'}</button>
              </form>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default OCRChatWidget;
